/**
 * CVO All Complaints View
 * Displays and manages all complaints
 * Self-contained view with its own state management (ready for API integration)
 */

import React, { useState, useRef, useEffect } from 'react';


import { exportToPDF, exportToExcel, exportToCSV } from '../../utils/tableExport';
import TablePagination from '../../components/TablePagination';

const ComplaintsView = ({
  complaints = [],
  onComplaintEdit,
  loading = false,
  error = null
}) => {
  // Local state for filtering and UI
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const dropdownRef = useRef(null);
  const downloadDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get columns for export
  const getColumns = () => [
    { field: 'created_at', headerName: 'Created Date' },
    { field: 'daysPassed', headerName: 'Days Passed' },
    { field: 'complaint_id', headerName: 'Complaint ID' },
    { field: 'description', headerName: 'Description' },
    { field: 'assignedOfficer', headerName: 'Assigned Officer' },
    { field: 'status', headerName: 'Status' },
    { field: 'priority', headerName: 'Priority' },
  ];

  // Get filtered data for export
  const getFilteredData = () => {
    return filteredComplaints.map(complaint => ({
      ...complaint,
      created_at: complaint.created_at ? new Date(complaint.created_at).toLocaleDateString() : '',
      status: complaint.status || '',
      priority: complaint.priority || '',
    }));
  };


  const handleExportExcel = () => {
    try {
      const filteredData = getFilteredData();
      exportToExcel(filteredData, getColumns(), 'cvo-complaints');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    }
  };

  const handleExportCSV = () => {
    try {
      const filteredData = getFilteredData();
      exportToCSV(filteredData, getColumns(), 'cvo-complaints');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const handleExportPDF = () => {
    try {
      const filteredData = getFilteredData();
      exportToPDF(filteredData, getColumns(), 'cvo-complaints');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };



  // Ensure complaints is always an array
  const complaintsArray = Array.isArray(complaints) ? complaints : [];

  // Transform API data to match expected format
  const transformedComplaints = complaintsArray.map(complaint => ({
    // Use DB id for DataGrid row identification
    id: complaint.id,
    complaint_id: complaint.complaint_id || '',
    description: complaint.description || complaint.your_complaint || '',
    date: complaint.date || complaint.created_at || '',
    created_at: complaint.created_at || complaint.date || '',
    status: complaint.status || '',
    assignedOfficer: complaint.assignedOfficer || '',
    priority: complaint.priority || ''
  }));

  // Filter complaints based on search term
  const filteredComplaints = transformedComplaints.filter(complaint => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const description = (complaint.description || '').toString().toLowerCase();
    const complaintId = (complaint.complaint_id || complaint.id || '').toString().toLowerCase();
    const status = (complaint.status || '').toString().toLowerCase();
    const assignedOfficer = (complaint.assignedOfficer || '').toString().toLowerCase();
    const priority = (complaint.priority || '').toString().toLowerCase();

    return description.includes(searchLower) ||
      complaintId.includes(searchLower) ||
      status.includes(searchLower) ||
      assignedOfficer.includes(searchLower) ||
      priority.includes(searchLower);
  });

  // Pagination Logic
  const totalItems = filteredComplaints.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedComplaints = filteredComplaints.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Header with Search (Left) and Buttons (Right) */}
      <div className="flex justify-between items-center pb-4 flex-shrink-0 gap-4">
        {/* Search Bar - Left Aligned */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-96">
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 outline-none text-gray-700"
              placeholder="Search by complaint ID, description, status, officer, or priority..."
            />
          </div>
        </div>

        {/* Buttons - Right Aligned */}
        <div className="flex items-center gap-3">
          {/* Download Button with Dropdown */}
          <div className="relative" ref={downloadDropdownRef}>
            <button
              onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download</span>
              <svg className={`w-4 h-4 transition-transform ${showDownloadDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Download Dropdown Menu */}
            {showDownloadDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <button
                    onClick={handleExportCSV}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download CSV</span>
                  </button>
                  <button
                    onClick={handleExportExcel}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download Excel</span>
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span>Download PDF</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Complaints Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 flex-1 flex flex-col overflow-hidden min-h-0">
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 relative">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Created Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Days Passed
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Complaint ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Assigned Officer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Priority
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="animate-spin h-8 w-8 text-primary-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <p className="text-gray-600">Loading complaints...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredComplaints.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    <p className="text-lg font-medium text-gray-900">No complaints found</p>
                    <p className="text-sm text-gray-500">Try adjusting your search criteria</p>
                  </td>
                </tr>
              ) : (
                paginatedComplaints.map((row) => {
                  // Date Formatting
                  let formattedDate = row.created_at;
                  try {
                    if (row.created_at) {
                      const d = new Date(row.created_at);
                      formattedDate = d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    }
                  } catch (e) { }

                  // Days Passed Calculation
                  let daysPassed = '';
                  try {
                    const dateValue = row.date || row.created_at;
                    if (dateValue) {
                      const created = new Date(dateValue);
                      daysPassed = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
                    }
                  } catch (e) { }

                  return (
                    <tr key={row.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formattedDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {daysPassed}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {row.complaint_id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={row.description}>
                        {row.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.assignedOfficer || <span className="text-gray-400">Unassigned</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const status = row.status;
                          const statusLower = status?.toLowerCase() || '';
                          let styles = "bg-gray-100 text-gray-800 border-gray-200";

                          if (['pending', 'under_review', 'under review'].includes(statusLower)) styles = "bg-yellow-50 text-yellow-800 border-yellow-200";
                          else if (statusLower === 'resolved') styles = "bg-green-50 text-green-700 border-green-200";
                          else if (statusLower === 'rejected') styles = "bg-red-50 text-red-700 border-red-200";

                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
                              {status || '—'}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const priority = row.priority;
                          const priorityLower = priority?.toLowerCase() || '';
                          let styles = "bg-gray-100 text-gray-800 border-gray-200";

                          if (priorityLower === 'high') styles = "bg-red-50 text-red-700 border-red-200";
                          else if (priorityLower === 'medium') styles = "bg-yellow-50 text-yellow-800 border-yellow-200";
                          else if (priorityLower === 'low') styles = "bg-green-50 text-green-700 border-green-200";

                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
                              {priority || '—'}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => onComplaintEdit && onComplaintEdit(row)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="Edit Complaint"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(newSize) => {
            setItemsPerPage(newSize);
            setCurrentPage(1);
          }}
        />
      </div>

    </div>
  );
};

export default ComplaintsView;

